import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import api, { apiErr, fmtDate } from "@/lib/api";
import { PageHero, Field, Loading } from "@/components/site/ui";
import { Reveal } from "@/components/brand";
import { SIGNATURE } from "@/constants/media";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none";

export default function Guestbook() {
  const [entries, setEntries] = useState(null);
  const [f, setF] = useState({ name: "", message: "", memory: "", visit_date: "", allow_public: true });
  const [sending, setSending] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const load = () => api.get("/guestbook").then((r) => setEntries(r.data)).catch(() => setEntries([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/guestbook", f);
      toast.success(data.message);
      setF({ name: "", message: "", memory: "", visit_date: "", allow_public: true });
    } catch (err) { toast.error(apiErr(err)); } finally { setSending(false); }
  };

  return (
    <div>
      <PageHero eyebrow="A Private Guestbook" title={SIGNATURE} compact />
      <div className="max-w-4xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-10">
        <div>
          <h3 className="font-serif text-3xl mb-6">Sign the guestbook</h3>
          <form onSubmit={submit} className="glass rounded-lg p-6 space-y-4">
            <Field label="Your name" required><input required className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="gb-name" /></Field>
            <Field label="Your message" required><textarea required rows={4} className={inputCls} value={f.message} onChange={(e) => set("message", e.target.value)} data-testid="gb-message" /></Field>
            <Field label="A favorite memory"><input className={inputCls} value={f.memory} onChange={(e) => set("memory", e.target.value)} /></Field>
            <Field label="Date of visit"><input type="date" className={inputCls} value={f.visit_date} onChange={(e) => set("visit_date", e.target.value)} /></Field>
            <label className="flex items-center gap-3 text-sm cursor-pointer">
              <input type="checkbox" checked={f.allow_public} onChange={(e) => set("allow_public", e.target.checked)} className="accent-gold" />
              Allow The D to display this publicly (after review)
            </label>
            <button disabled={sending} className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2" data-testid="gb-submit">
              {sending ? <Loader2 className="animate-spin" size={16} /> : null} Sign
            </button>
          </form>
        </div>
        <div>
          <h3 className="font-serif text-3xl mb-6">From our guests</h3>
          {entries === null ? <Loading /> : entries.length === 0 ? (
            <p className="text-muted-foreground text-sm glass rounded-lg p-6">Be the first to leave a note. Approved entries will appear here.</p>
          ) : (
            <div className="space-y-4">
              {entries.map((e, i) => (
                <Reveal key={e.id} delay={(i % 4) * 0.05}>
                  <div className="glass rounded-lg p-6">
                    <p className="font-serif text-lg italic">"{e.message}"</p>
                    <p className="text-xs text-gold mt-3">— {e.name}{e.visit_date ? ` · ${fmtDate(e.visit_date)}` : ""}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
